import { BufferGeometry } from 'three/src/core/BufferGeometry';
import { Box3 } from 'three/src/math/Box3';
import { Vector3 } from 'three/src/math/Vector3';
import { Raycaster } from 'three/src/core/Raycaster';
import { MeshBasicMaterial } from 'three/src/materials/MeshBasicMaterial';
import { DoubleSide } from 'three/src/constants';
import { Mesh } from 'three/src/objects/Mesh';

/**
 * Shared geometry primitives for the stope fill: triangle extraction, plain mesh volume, the discharge
 * probe and the default anchor.
 *
 * Nothing here knows about beaches - the fill MODEL lives in fillSurface.ts. Keep it that way rather
 * than growing a second fill model alongside the first.
 */

export interface FillAnchor {
  x: number;
  z: number;
}

// Flattened triangle vertices (9 doubles per triangle) are derived once per geometry and reused by every
// curve built against it. A stope is walked repeatedly - once per pour, plus a preview on every keystroke
// - so re-reading the attribute buffers each time dominated the cost of the integral itself.
const triangleCache = new WeakMap<BufferGeometry, Float64Array>();

export const extractTriangles = (geometry: BufferGeometry): Float64Array => {
  const cached = triangleCache.get(geometry);
  if (cached) return cached;

  const position = geometry.getAttribute('position');
  if (!position) {
    const empty = new Float64Array(0);
    triangleCache.set(geometry, empty);
    return empty;
  }

  const { index } = geometry;
  const triangleCount = index ? Math.floor(index.count / 3) : Math.floor(position.count / 3);
  const out = new Float64Array(triangleCount * 9);

  Array.from({ length: triangleCount }).forEach((unused, triangle) => {
    const base = triangle * 3;
    [0, 1, 2].forEach((corner) => {
      const vertex = index ? index.getX(base + corner) : base + corner;
      const offset = triangle * 9 + corner * 3;
      out[offset] = position.getX(vertex);
      out[offset + 1] = position.getY(vertex);
      out[offset + 2] = position.getZ(vertex);
    });
  });

  triangleCache.set(geometry, out);
  return out;
};

// Divergence-theorem contribution of one triangle under F = (x, 0, 0): the integral of div F over the
// solid equals the integral of F.n over the boundary, which per triangle is the x-centroid times the
// signed area of its projection onto the yz plane.
const enclosedVolumeContribution = (
  ax: number, ay: number, az: number,
  bx: number, by: number, bz: number,
  cx: number, cy: number, cz: number,
): number => {
  const uy = by - ay;
  const uz = bz - az;
  const vy = cy - ay;
  const vz = cz - az;
  return ((uy * vz - uz * vy) / 2) * ((ax + bx + cx) / 3);
};

/** Enclosed volume of a closed mesh, over the shared triangle extraction. */
export const trianglesVolume = (triangles: Float64Array): number => {
  let total = 0;
  for (let i = 0; i < triangles.length; i += 9) {
    total += enclosedVolumeContribution(
      triangles[i], triangles[i + 1], triangles[i + 2],
      triangles[i + 3], triangles[i + 4], triangles[i + 5],
      triangles[i + 6], triangles[i + 7], triangles[i + 8],
    );
  }
  return Math.abs(total);
};

export const meshVolume = (mesh: Mesh): number => trianglesVolume(extractTriangles(mesh.geometry));

const geometryBoundingBox = (mesh: Mesh): Box3 => {
  mesh.geometry.computeBoundingBox();
  return mesh.geometry.boundingBox!;
};

export const getGlobalMinY = (mesh: Mesh): number => geometryBoundingBox(mesh).min.y;

/** The default discharge anchor: the geometry bounding box centre in x/z. */
export const defaultFillAnchor = (mesh: Mesh): FillAnchor => {
  const box = geometryBoundingBox(mesh);
  return { x: (box.min.x + box.max.x) / 2, z: (box.min.z + box.max.z) / 2 };
};

const sharedRaycaster = new Raycaster();

// A standalone mesh is used for the probe so the raycast runs in GEOMETRY space: the rendered stope is
// parented to a <group> carrying the display scale/offset, and intersectObject would otherwise return
// world-space hits while every fill calculation works in local coordinates.
//
// DoubleSide is equally load-bearing. The default FrontSide material back-face-culls, and on a cleanly
// outward-wound closed solid the floor faces point straight DOWN - the same direction the probe ray
// travels - so they get culled and only the roof registers. The probe would then report the ceiling as
// the floor and a single pour would swallow the whole stope.
const probeMesh = new Mesh(undefined, new MeshBasicMaterial({ side: DoubleSide }));

/**
 * The floor of the solid directly beneath the discharge pipe. This is the datum the FIRST pour is
 * measured from (see the module note in pour.ts).
 *
 * Deliberately NOT the level at which the enclosed volume is zero. Most stopes carry a drawpoint or
 * undercut lobe hanging below the main void, so the V = 0 level sits several metres under the pipe
 * floor; measuring pour 1 from there would attribute that lobe to it and shrink a nominal 5 m first
 * pour by more than half.
 *
 * MUST be called with the PRISTINE repaired stope (see geometryRepair). Probing an accumulated CSG
 * "remaining" mesh is what produced the historical mis-stacking bug: a boolean against real stope
 * geometry can leave the original floor faces behind, so the probe answers with the ORIGINAL base no
 * matter how much has already been poured. Only pour 1 is probed against the bare solid; every later pour
 * measures from the fill surface already under its pipe (see dischargeDatum).
 */
export const dischargeFloorY = (mesh: Mesh, anchor: FillAnchor): number => {
  const box = geometryBoundingBox(mesh);
  sharedRaycaster.set(new Vector3(anchor.x, box.max.y + 0.01, anchor.z), new Vector3(0, -1, 0));
  probeMesh.geometry = mesh.geometry;
  const hits = sharedRaycaster.intersectObject(probeMesh, true);

  // No solid under the discharge column: fall back to the bottom of the box so the first pour starts
  // from the lowest fillable point rather than floating under the pipe.
  if (hits.length === 0) return box.min.y;

  // Hits are ordered nearest -> farthest from the origin, so the last one is the floor of the column.
  const floor = hits[hits.length - 1].point.y;

  // The column only grazes the very top (no real span below the pipe): same fallback.
  if (floor >= box.max.y) return box.min.y;

  return floor;
};
